import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { RevealOnScroll } from "./RevealOnScroll";

export type Crumb = { label: string; to?: string };

export type ConversionPath = {
  eyebrow: string;
  label: string;
  to: string;
  search?: Record<string, string>;
  emphasized?: boolean;
};

export type HeroCta = {
  eyebrow?: string;
  label: string;
  to: string;
  search?: Record<string, string>;
};

type Props = {
  number?: string;
  eyebrow?: string;
  headline: ReactNode;
  lead?: string;
  mobileLead?: string;
  metaStrip?: string[];
  secondaryMeta?: string[];
  conversionPaths?: ConversionPath[];
  primaryCta?: HeroCta;
  secondaryCta?: { label: string; to: string; search?: Record<string, string> };
  breadcrumbs?: Crumb[];
  variant?: "full" | "short";
  compactMobile?: boolean;
};

export function Hero({
  number = "01",
  eyebrow,
  headline,
  lead,
  mobileLead,
  metaStrip,
  secondaryMeta,
  conversionPaths,
  primaryCta,
  secondaryCta,
  breadcrumbs,
  variant = "full",
  compactMobile = false,
}: Props) {
  return (
    <section
      className={`relative overflow-hidden text-white ${
        variant === "full" ? "pt-32 pb-24 md:pt-44 md:pb-32" : "pt-28 pb-20 md:pt-40 md:pb-24"
      }`}
      style={{
        background:
          "linear-gradient(135deg, oklch(0.62 0.13 220) 0%, oklch(0.45 0.12 225) 100%)",
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <div className="relative mx-auto grid max-w-[1200px] grid-cols-1 gap-6 px-6 md:grid-cols-[64px_minmax(0,1fr)] md:gap-10 md:px-12">
        <div
          aria-hidden
          className="font-mono text-xs tracking-wider leading-[1.35] text-white/75"
        >
          {number}
        </div>
        <div className="min-w-0 max-w-4xl">
          {eyebrow && (
            <RevealOnScroll className="mb-6">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-white/85">
                {eyebrow}
              </p>
            </RevealOnScroll>
          )}
          <RevealOnScroll>
            <h1
              className={`font-display editorial-italic text-white [overflow-wrap:normal] [word-break:normal] hyphens-none ${
                compactMobile ? "hero-compact-mobile" : "text-hero"
              }`}
            >
              {headline}
            </h1>
          </RevealOnScroll>
          {lead && (
            <RevealOnScroll delay={120}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 md:mt-8 md:text-lg">
                {mobileLead ? (
                  <>
                    <span className="md:hidden">{mobileLead}</span>
                    <span className="hidden md:inline">{lead}</span>
                  </>
                ) : (
                  lead
                )}
              </p>
            </RevealOnScroll>
          )}

          {metaStrip && (
            <RevealOnScroll delay={180}>
              <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-wider text-white/85 md:mt-10 md:text-xs">
                {metaStrip.map((m, i) => (
                  <span key={m} className="flex items-center gap-3">
                    <span>{m}</span>
                    {i < metaStrip.length - 1 && <span className="text-white/60">/</span>}
                  </span>
                ))}
              </div>
            </RevealOnScroll>
          )}
          {secondaryMeta && (
            <RevealOnScroll delay={220}>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-white/55 md:text-[11px]">
                {secondaryMeta.map((m, i) => (
                  <span key={m} className="flex items-center gap-3">
                    <span>{m}</span>
                    {i < secondaryMeta.length - 1 && <span className="text-white/40">/</span>}
                  </span>
                ))}
              </div>
            </RevealOnScroll>
          )}

          {primaryCta && (
            <RevealOnScroll delay={260}>
              <div className="mt-10 md:mt-12">
                {primaryCta.eyebrow && (
                  <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-lime">
                    {primaryCta.eyebrow}
                  </p>
                )}
                <Link
                  to={primaryCta.to}
                  search={primaryCta.search as never}
                  className="inline-flex items-center gap-2 rounded-md bg-lime px-7 py-4 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90 md:px-8 md:py-5 md:text-base"
                >
                  <span>{primaryCta.label}</span>
                  <span aria-hidden>→</span>
                </Link>
                {secondaryCta && (
                  <div className="mt-5">
                    <Link
                      to={secondaryCta.to}
                      search={secondaryCta.search as never}
                      className="inline-flex items-center gap-2 text-sm text-white/75 underline-offset-4 transition-colors hover:text-white hover:underline"
                    >
                      <span>{secondaryCta.label}</span>
                      <span aria-hidden>→</span>
                    </Link>
                  </div>
                )}
              </div>
            </RevealOnScroll>
          )}

          {conversionPaths && conversionPaths.length > 0 && !primaryCta && (
            <RevealOnScroll delay={260}>
              <div className="mt-8 md:mt-10">
                <ul className="flex flex-col gap-5 border-y border-white/15 py-5 md:flex-row md:items-stretch md:gap-0 md:divide-x md:divide-white/15 md:py-6">
                  {conversionPaths.map((p) => (
                    <li
                      key={p.to + p.label}
                      className="md:flex-1 md:px-6 md:first:pl-0 md:last:pr-0"
                    >
                      <p
                        className={`font-mono text-[11px] uppercase tracking-[0.16em] ${
                          p.emphasized ? "text-lime" : "text-white/65"
                        }`}
                      >
                        {p.eyebrow}
                      </p>
                      <Link
                        to={p.to}
                        search={p.search as never}
                        className={`mt-2 inline-flex items-center gap-2 border-b border-white/40 pb-1 leading-snug text-white transition-colors hover:border-lime hover:text-lime ${
                          p.emphasized ? "text-[17px] font-medium md:text-lg" : "text-[15px] md:text-base"
                        }`}
                      >
                        <span>{p.label}</span>
                        <span aria-hidden>→</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </RevealOnScroll>
          )}

          {breadcrumbs && (
            <nav className="mt-10 flex items-center gap-2 font-mono text-xs text-white/85">
              {breadcrumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-2">
                  {c.to ? (
                    <Link to={c.to} className="hover:text-white">{c.label}</Link>
                  ) : (
                    <span className="text-white">{c.label}</span>
                  )}
                  {i < breadcrumbs.length - 1 && <span>›</span>}
                </span>
              ))}
            </nav>
          )}
        </div>
      </div>
    </section>
  );
}
